import React from 'react';
import Button from '../../atoms/Button/Button';
import ListSimpleDropdown from '../ListSimpleDropdown/ListSimpleDropdown';
import Popover from '../Popover/Popover';
import type { ActionButtonDropdownMenuProps } from './types';
import useActionButtonDropdownMenu from './useActionButtonDropdownMenu';

/**
 * ActionButtonDropdownMenu — v2 molecule (antes `ButtonFixedDropdown`).
 *
 * Menú de acciones con el disparador de botón (icono) soldado dentro. Compone
 * `Button` (disparador) + `Popover` (panel flotante v2) + `ListSimpleDropdown`
 * (v2, patrón menú WAI-ARIA) o un `customDropdown` libre. La API replica la del
 * v1 para que la migración de consumidores sea drop-in.
 *
 * A11y: el disparador expone `aria-haspopup` (`dialog` con `customDropdown`,
 * `menu` con `options`) y `aria-expanded`, y el `Popover` gestiona
 * Escape/click-away y la devolución de foco. Con `options`, el foco se mueve a
 * la primera fila del menú al abrir (patrón APG "menu button") y el contenido
 * es navegable por teclado (flechas/Home/End/type-ahead, TAN-9065); con
 * `customDropdown` el foco va al panel. `ariaLabel` es obligatorio: es el
 * nombre accesible del disparador icon-only (WCAG 4.1.2).
 */
function ActionButtonDropdownMenu({
  ariaLabel,
  classNameDropdown = '',
  closeOnClickInOption = true,
  customDropdown,
  emitClickEventOpen = () => {},
  fixed = true,
  icon = 'add',
  iconClose,
  iconColor = 'primary',
  iconSize,
  onChangeOpen = () => {},
  open,
  options = [],
  placementDropdown,
  rotate = true,
  size = 'l',
}: Readonly<ActionButtonDropdownMenuProps>): JSX.Element {
  const {
    anchorEl,
    ariaHasPopup,
    buttonClassName,
    containerButtonRef,
    iconButton,
    isOpen,
    onClickInOption,
    onCloseDropdown,
    onToggleDropdown,
    popoverClassName,
    resolvedIconSize,
    rootClassName,
    shouldCloseOnTabOut,
    shouldFocusPanelOnOpen,
  } = useActionButtonDropdownMenu({
    classNameDropdown,
    closeOnClickInOption,
    customDropdown,
    emitClickEventOpen,
    fixed,
    icon,
    iconClose,
    iconSize,
    onChangeOpen,
    open,
    rotate,
    size,
  });

  return (
    <div className={rootClassName}>
      <div className={buttonClassName} ref={containerButtonRef}>
        <Button ariaExpanded={isOpen} ariaHasPopup={ariaHasPopup} ariaLabel={ariaLabel} iconSize={resolvedIconSize} isIconOnly leftIcon={iconButton} onClick={onToggleDropdown} />
      </div>
      {isOpen && anchorEl && (
        <Popover
          anchorEl={anchorEl}
          className={popoverClassName}
          handleClose={onCloseDropdown}
          open={isOpen}
          placement={placementDropdown}
          shouldCloseOnTabOut={shouldCloseOnTabOut}
          shouldFocusOnOpen={shouldFocusPanelOnOpen}
        >
          {customDropdown ?? (
            <ListSimpleDropdown ariaLabel={ariaLabel} colorIcon={iconColor} items={options} onClick={onClickInOption} shouldAutoFocusFirstItem />
          )}
        </Popover>
      )}
    </div>
  );
}

export default ActionButtonDropdownMenu;
